import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Complaint, ComplaintStatus } from '../../core/models/complaint.model';
import { AuthService } from '../../core/services/auth.service';
import { ComplaintService } from '../../core/services/complaint.service';
import { OpsService } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';
import { COLUMNS, ColumnDef, buildSearchText, fmtDate, statusTone } from './complaint-fields';

interface AssignableRole {
  id: string | number;
  roleName: string;
  description?: string;
  holderUserId?: string | number | null;
  holderName?: string | null;
  holderDepartment?: string | null;
}

const EXTRA_COLUMNS_STORAGE_KEY = 'complaints.extraColumns';
const HISTORY_STATUSES: ComplaintStatus[] = ['resolved', 'closed'];

/** Statuses where DM/Admin may still assign an officer. */
const ASSIGNABLE_STATUSES: ComplaintStatus[] = ['submitted', 'reopened'];

export function caseBelongsToView(
  status: ComplaintStatus,
  view: 'active' | 'history',
): boolean {
  const isHistory = HISTORY_STATUSES.includes(status);
  return view === 'history' ? isHistory : !isHistory;
}

/** Assign enabled for submitted and reopened; disabled for all other statuses. */
export function canShowAssign(status: ComplaintStatus): boolean {
  return ASSIGNABLE_STATUSES.includes(status);
}

function csvEscape(value: string): string {
  const s = value ?? '';
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function loadStoredExtraColumns(): Set<string> {
  try {
    const raw = localStorage.getItem(EXTRA_COLUMNS_STORAGE_KEY);
    const keys = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set(keys.filter((k) => COLUMNS.some((c) => c.extra && c.key === k)));
  } catch {
    return new Set();
  }
}

@Component({
  selector: 'app-complaint-list',
  standalone: true,
  imports: [RouterLink, FormsModule],
  templateUrl: './complaint-list.component.html',
  styleUrl: './complaint-list.component.scss',
})
export class ComplaintListComponent {
  private readonly store = inject(ComplaintService);
  private readonly ops = inject(OpsService);
  private readonly auth = inject(AuthService);
  private readonly rbac = inject(RbacService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly qParam = toSignal(this.route.queryParamMap.pipe(map((p) => p.get('q') ?? '')), {
    initialValue: '',
  });
  private readonly statusParam = toSignal(
    this.route.queryParamMap.pipe(map((p) => p.get('status') ?? '')),
    { initialValue: '' },
  );

  readonly query = signal('');
  readonly status = signal<'All' | ComplaintStatus>('All');
  readonly view = signal<'active' | 'history'>('active');
  /** Quick filter from KPI tiles (null = no tile focus). */
  readonly kpiFocus = signal<'total' | 'pending' | 'resolved' | null>(null);
  readonly caseType = signal('All');
  readonly assignedRole = signal('All');
  readonly dateFrom = signal('');
  readonly dateTo = signal('');
  /** Values for column-synced filters (Department, Tehsil, Category, text fields, …). */
  readonly columnFilterValues = signal<Record<string, string>>({});
  readonly page = signal(1);
  readonly pageSize = 5;
  readonly message = signal<string | null>(null);

  readonly columnPickerOpen = signal(false);
  readonly enabledExtraColumns = signal<Set<string>>(loadStoredExtraColumns());
  readonly extraColumnDefs = COLUMNS.filter((c) => c.extra);
  /** Extra columns that have a filter and are currently enabled in "+ Columns". */
  readonly activeColumnFilters = computed(() => {
    const enabled = this.enabledExtraColumns();
    return COLUMNS.filter((c) => c.extra && c.filter && enabled.has(c.key));
  });

  /** True when any list filter differs from its default (columns stay as-is). */
  readonly hasActiveFilters = computed(() => {
    if (this.query().trim() || this.qParam().trim()) return true;
    if (this.status() !== 'All') return true;
    if (this.caseType() !== 'All') return true;
    if (this.assignedRole() !== 'All') return true;
    if (this.dateFrom() || this.dateTo()) return true;
    const values = this.columnFilterValues();
    for (const col of this.activeColumnFilters()) {
      const v = (values[col.key] ?? '').trim();
      if (col.filter === 'select' && v && v !== 'All') return true;
      if (col.filter === 'text' && v) return true;
    }
    return false;
  });
  readonly statusTone = statusTone;
  readonly fmtDate = fmtDate;
  readonly canShowAssign = canShowAssign;

  readonly assignOpen = signal(false);
  readonly assignComplaintId = signal<string | number | null>(null);
  readonly assignRoles = signal<AssignableRole[]>([]);
  readonly selectedRole = signal('');
  readonly assignNote = signal('');
  readonly assignDueDays = signal(7);
  readonly assignBusy = signal(false);

  readonly dueDayOptions = [3, 5, 7, 10, 15];

  /** Investigator desk (officer or Admin-created role) sees only assigned complaints. */
  readonly isAssignedOnly = computed(() => this.rbac.isAssigneeScoped());
  readonly isDm = computed(() => this.rbac.role() === 'dm');
  readonly canAssign = computed(() => this.rbac.canWorkflowAction('Assign Officer'));

  constructor() {
    const q = this.qParam();
    if (q) this.query.set(q);
    const status = this.statusParam() as ComplaintStatus;
    if (status && this.statuses.includes(status)) {
      this.status.set(status);
      if (!this.isDm() && HISTORY_STATUSES.includes(status)) this.view.set('history');
    }
    if (this.isDm()) this.view.set('active');
  }

  readonly statuses: Array<'All' | ComplaintStatus> = [
    'All',
    'submitted',
    'pending_verification',
    'under_review',
    'routed',
    'investigation_completed',
    'awaiting_hearing',
    'scheduled',
    'heard',
    'resolved',
    'closed',
    'rejected',
    'reopened',
  ];

  readonly visibleStatuses = computed(() =>
    this.statuses.filter((status) => {
      if (status === 'All') return true;
      const isHistory = HISTORY_STATUSES.includes(status);
      return this.view() === 'history' ? isHistory : !isHistory;
    }),
  );

  readonly caseTypes = computed(() => {
    const set = new Set<string>();
    for (const item of this.store.list()) {
      if (item.category && item.category !== '—') set.add(item.category);
    }
    return ['All', ...Array.from(set).sort()];
  });

  /** Live options for each select-type column filter (keyed by column key). */
  readonly columnSelectOptions = computed(() => {
    const map: Record<string, string[]> = {};
    for (const col of COLUMNS) {
      if (col.filter !== 'select') continue;
      const set = new Set<string>();
      let hasUnassigned = false;
      for (const item of this.store.list()) {
        const raw = this.columnFilterRaw(item, col);
        if (!raw) {
          if (col.key === 'department') hasUnassigned = true;
          continue;
        }
        set.add(raw);
      }
      const options = ['All', ...Array.from(set).sort()];
      if (col.key === 'department' && hasUnassigned) options.push('Unassigned');
      map[col.key] = options;
    }
    return map;
  });

  /** Desk roles actually assigned on at least one complaint (tehsildar, sdm, officer, ...) —
   *  more useful than free-text search since complaints are assigned to a specific desk role. */
  readonly assignedRoleOptions = computed(() => {
    const set = new Set<string>();
    for (const item of this.store.list()) {
      const role = String(item.assignedRole || '').trim();
      if (role) set.add(role);
    }
    return ['All', ...Array.from(set).sort()];
  });

  readonly visibleColumns = computed(() => {
    const enabled = this.enabledExtraColumns();
    return COLUMNS.filter(
      (c) => (!c.extra || enabled.has(c.key)) && !(this.isDm() && c.key === 'assignedTo'),
    );
  });

  /** Rows this role is allowed to see at all — before the user's own search/status/date filters. */
  readonly scopedList = computed(() => {
    const myId = this.auth.officer()?.id;
    const assignedOnly = this.isAssignedOnly();
    return this.store.list().filter((item) => {
      if (assignedOnly) {
        const assigned = item.assignedOfficerRef ?? item.assignedRole;
        if (myId == null || String(assigned) !== String(myId)) return false;
      }
      return true;
    });
  });

  /** Active work excludes completed cases; History contains resolved and closed cases only. */
  readonly viewList = computed(() =>
    this.scopedList().filter((item) => caseBelongsToView(item.status, this.view())),
  );

  readonly kpiTotal = computed(() => this.scopedList().length);
  readonly kpiPending = computed(
    () =>
      this.scopedList().filter((c) =>
        ['submitted', 'pending_verification', 'under_review'].includes(c.status),
      ).length,
  );
  readonly kpiResolved = computed(
    () => this.scopedList().filter((c) => ['resolved', 'closed'].includes(c.status)).length,
  );

  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase() || this.qParam().trim().toLowerCase();
    const columnFilters = this.activeColumnFilters();
    const filterValues = this.columnFilterValues();
    return this.viewList().filter((item) => {
      const focus = this.kpiFocus();
      if (focus === 'pending') {
        if (!['submitted', 'pending_verification', 'under_review'].includes(item.status)) {
          return false;
        }
      }
      const matchesStatus = this.status() === 'All' || item.status === this.status();
      const matchesCaseType = this.caseType() === 'All' || item.category === this.caseType();
      const matchesAssignedRole =
        this.assignedRole() === 'All' || String(item.assignedRole || '').trim() === this.assignedRole();
      const matchesColumnFilters = columnFilters.every((col) =>
        this.matchesColumnFilter(item, col, filterValues[col.key]),
      );
      const day = (item.submittedAt || '').slice(0, 10);
      const matchesFrom = !this.dateFrom() || day >= this.dateFrom();
      const matchesTo = !this.dateTo() || day <= this.dateTo();
      const matchesQuery = !q || buildSearchText(item).includes(q);
      return (
        matchesStatus &&
        matchesCaseType &&
        matchesAssignedRole &&
        matchesColumnFilters &&
        matchesFrom &&
        matchesTo &&
        matchesQuery
      );
    });
  });

  /** Normalised cell value used for column filter matching / option lists. */
  private columnFilterRaw(item: Complaint, col: ColumnDef): string {
    if (col.key === 'department') {
      const dept = String(item.department || '').trim();
      return !dept || dept === '—' ? '' : dept;
    }
    const v = col.get(item);
    if (v == null || v === '—') return '';
    return String(v).trim();
  }

  private matchesColumnFilter(item: Complaint, col: ColumnDef, rawValue: string | undefined): boolean {
    const value = (rawValue ?? (col.filter === 'text' ? '' : 'All')).trim();
    if (col.filter === 'select') {
      if (!value || value === 'All') return true;
      if (col.key === 'department' && value === 'Unassigned') {
        return !this.columnFilterRaw(item, col);
      }
      return this.columnFilterRaw(item, col) === value;
    }
    // text
    if (!value) return true;
    return this.columnFilterRaw(item, col).toLowerCase().includes(value.toLowerCase());
  }

  columnFilterValue(key: string): string {
    const col = COLUMNS.find((c) => c.key === key);
    const stored = this.columnFilterValues()[key];
    if (stored != null) return stored;
    return col?.filter === 'text' ? '' : 'All';
  }

  setColumnFilter(key: string, value: string): void {
    this.columnFilterValues.update((m) => ({ ...m, [key]: value }));
    this.page.set(1);
  }

  private clearColumnFilter(key: string): void {
    this.columnFilterValues.update((m) => {
      if (!(key in m)) return m;
      const next = { ...m };
      delete next[key];
      return next;
    });
  }

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filtered().length / this.pageSize)));

  readonly paged = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filtered().slice(start, start + this.pageSize);
  });

  serialNo(item: Complaint): number {
    return this.filtered().indexOf(item) + 1;
  }

  switchView(view: 'active' | 'history'): void {
    this.view.set(view);
    this.status.set('All');
    this.kpiFocus.set(view === 'history' ? 'resolved' : null);
    this.page.set(1);
  }

  /** KPI tiles: filter the list to that bucket. */
  selectKpi(focus: 'total' | 'pending' | 'resolved'): void {
    this.kpiFocus.set(focus);
    this.status.set('All');
    this.page.set(1);
    if (focus === 'resolved') {
      this.view.set('history');
    } else {
      this.view.set('active');
    }
  }

  toggleColumnPicker(): void {
    this.columnPickerOpen.update((v) => !v);
  }

  isColumnEnabled(key: string): boolean {
    return this.enabledExtraColumns().has(key);
  }

  toggleColumn(key: string): void {
    const wasEnabled = this.enabledExtraColumns().has(key);
    this.enabledExtraColumns.update((set) => {
      const next = new Set(set);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(EXTRA_COLUMNS_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        /* localStorage unavailable — column choice just won't persist across visits */
      }
      return next;
    });
    // Drop any active filter for a column that was just unchecked.
    if (wasEnabled) {
      this.clearColumnFilter(key);
      this.page.set(1);
    }
  }

  /** Reset filter values only — does not change which columns are enabled. */
  resetFilters(): void {
    this.query.set('');
    this.status.set('All');
    this.caseType.set('All');
    this.assignedRole.set('All');
    this.dateFrom.set('');
    this.dateTo.set('');
    this.columnFilterValues.set({});
    this.page.set(1);
    // Clear URL search/status so filtered() does not keep applying qParam/statusParam.
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: null, status: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  exportCsv(): void {
    const rows = this.filtered();
    const cols = this.visibleColumns();
    const header = ['S.No.', 'Complaint ID', ...cols.map((c) => c.label), 'Date', 'Status'];
    const lines = [header.map(csvEscape).join(',')];
    rows.forEach((item, i) => {
      const values = [
        String(i + 1),
        String(item.trackingId ?? item.id),
        ...cols.map((c) => c.get(item)),
        fmtDate(item.submittedAt),
        item.status,
      ];
      lines.push(values.map(csvEscape).join(','));
    });
    // Leading UTF-8 BOM so Excel renders the Hindi (Devanagari) text correctly instead of mojibake.
    const BOM = String.fromCharCode(0xfeff);
    const csv = BOM + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `complaints-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    this.message.set(`Exported ${rows.length} complaint(s) to CSV.`);
  }

  async openAssign(id: string | number): Promise<void> {
    if (!this.canAssign()) {
      this.message.set('Only DM or Admin can assign complaints to a role.');
      return;
    }
    const item = this.scopedList().find((c) => String(c.id) === String(id));
    if (item && !canShowAssign(item.status)) {
      this.message.set('Assign is only available for submitted or reopened cases.');
      return;
    }
    this.assignComplaintId.set(id);
    this.selectedRole.set('');
    this.assignNote.set('');
    this.assignDueDays.set(7);
    this.assignOpen.set(true);
    this.message.set(null);
    try {
      const roles = await this.ops.listAssignableRoles();
      this.assignRoles.set(roles);
      const firstWithHolder = roles.find((r) => r.holderUserId);
      if (firstWithHolder) this.selectedRole.set(firstWithHolder.roleName);
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Failed to load roles');
      this.assignOpen.set(false);
    }
  }

  closeAssign(): void {
    this.assignOpen.set(false);
    this.assignComplaintId.set(null);
  }

  async confirmAssign(): Promise<void> {
    const id = this.assignComplaintId();
    const role = this.selectedRole().trim();
    if (!id || !role) {
      this.message.set('Select a role to assign.');
      return;
    }
    const picked = this.assignRoles().find((r) => r.roleName === role);
    if (picked && !picked.holderUserId) {
      this.message.set(
        `No officer holds “${role}”. Create a desk profile with that role in Settings first.`,
      );
      return;
    }
    this.assignBusy.set(true);
    try {
      this.message.set(
        await this.ops.assignComplaintToRole(
          id,
          role,
          this.assignNote().trim() || undefined,
          this.assignDueDays(),
        ),
      );
      await this.store.refresh();
      this.closeAssign();
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Assign failed');
    } finally {
      this.assignBusy.set(false);
    }
  }
}
